import { useCallback, useEffect, useState, useMemo } from 'react';

import { QuickActionLogicProps } from '@components/suggestions/quickActions/types';
import { HubspotCompany, HubspotProviderOptions } from '@components/suggestions/quickActions/types';

import {
  CrmEventDto,
  AccountUserDto,
  CrmEventDtoType,
  CreateHubspotEngagementMutationResult,
  EmailMessageDto,
  useGetMeetingAnalysis
} from '@/api/generated';
import useCalendar from '@/hooks/useCalendar';
import useHubspotEngagements from '@/hooks/useHubspotEngagements';
import HubspotEngagementDescription from '@/utils/htmlUtil';
import { launchErrorToast } from '@/utils/toast';

import useSetAction from './useSetAction';

export const useSummaryQuickActionLogic = ({ quickAction, index }: QuickActionLogicProps) => {
  const { data: meetingAnalysis, isError: isMeetingAnalysisError } = useGetMeetingAnalysis(
    quickAction.sourceId || ''
  );
  const [createEngagementData, setCreateEngagementData] = useState<CrmEventDto>({});

  const { createHubspotEngagement, hubspotContacts, hubspotCompanies } = useHubspotEngagements();

  const { event, isEventError } = useCalendar(
    0,
    0,
    meetingAnalysis?.items?.[0].calendarEventId || ''
  );

  const [provider, setProvider] = useState<string>('Hubspot');
  const [users, setUsers] = useState<AccountUserDto[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<AccountUserDto[]>([]);
  const [companies, setCompanies] = useState<HubspotCompany[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<HubspotCompany[]>([]);
  const [providerOption, setProviderOption] = useState<string>(HubspotProviderOptions.Company);

  const actionFunction = useCallback(async (): Promise<
    CreateHubspotEngagementMutationResult | EmailMessageDto | undefined
  > => {
    if (!isMeetingAnalysisError && createEngagementData?.meeting?.title) {
      return createHubspotEngagement(createEngagementData);
    }
    return Promise.reject();
  }, [createEngagementData, createHubspotEngagement, isMeetingAnalysisError]);

  useSetAction(
    actionFunction,
    index,
    isMeetingAnalysisError ||
      isEventError ||
      (providerOption === HubspotProviderOptions.Company && !selectedCompanies.length) ||
      (providerOption === HubspotProviderOptions.Contacts && !selectedUsers.length)
  );

  useEffect(() => {
    if (isEventError || isMeetingAnalysisError) {
      launchErrorToast();
    }
  }, [isEventError, isMeetingAnalysisError]);

  useEffect(() => {
    if (hubspotContacts?.items) {
      setUsers(
        hubspotContacts.items.map(contact => ({
          id: contact.id || '',
          email: contact.emails?.[0]?.email || ''
        }))
      );
    }
  }, [hubspotContacts]);

  useEffect(() => {
    if (hubspotCompanies?.items) {
      setCompanies(
        hubspotCompanies.items.map(company => ({
          id: company.id || '',
          name: company.name || ''
        }))
      );
    }
  }, [hubspotCompanies]);

  useEffect(() => {
    if (providerOption === HubspotProviderOptions.Company) {
      setSelectedUsers([]);
    } else {
      setSelectedCompanies([]);
    }
  }, [providerOption]);

  const htmlDescription = useMemo(
    () =>
      HubspotEngagementDescription(
        event?.title,
        meetingAnalysis?.items?.[0],
        event?.participants
          ?.map(participant => participant.email)
          .filter((email): email is string => email !== undefined)
      ),
    [event?.title, meetingAnalysis?.items, event?.participants]
  );

  useEffect(() => {
    const data = {
      companyIds:
        providerOption === HubspotProviderOptions.Company
          ? selectedCompanies.map(company => company.id)
          : [],
      contactIds:
        providerOption === HubspotProviderOptions.Contacts
          ? selectedUsers.map(user => user.id)
          : [],
      meeting: {
        description: htmlDescription,
        startAt: meetingAnalysis?.items?.[0].createdAt,
        endAt: meetingAnalysis?.items?.[0].createdAt,
        title: event?.title
      },
      type: CrmEventDtoType.meeting
    };
    setCreateEngagementData(data);
  }, [
    event,
    meetingAnalysis,
    hubspotCompanies?.items,
    selectedUsers,
    selectedCompanies,
    setCreateEngagementData,
    providerOption,
    htmlDescription
  ]);

  return {
    meetingAnalysis,
    event,
    provider,
    providerOption,
    setProviderOption,
    selectedUsers,
    setSelectedUsers,
    selectedCompanies,
    setSelectedCompanies
  };
};
